// Choices for the legal aid application's background and language questions.
//
// These were free text, so the same country or language arrived spelled several ways and
// nothing could be counted or matched on. A list fixes that, but a list must never quietly
// drop an answer somebody already gave - see withCurrentValue below.

// Australia first because it is the common answer; the rest alphabetical.
export const COUNTRIES = [
  "Australia",
  "Afghanistan", "Albania", "Algeria", "Angola", "Argentina", "Armenia", "Austria",
  "Azerbaijan", "Bangladesh", "Belarus", "Belgium", "Bhutan", "Bolivia",
  "Bosnia and Herzegovina", "Brazil", "Bulgaria", "Burundi", "Cambodia", "Cameroon",
  "Canada", "Chile", "China", "Colombia", "Congo", "Croatia", "Cuba", "Cyprus",
  "Czech Republic", "Democratic Republic of the Congo", "Denmark", "Ecuador", "Egypt",
  "El Salvador", "Eritrea", "Estonia", "Ethiopia", "Fiji", "Finland", "France", "Georgia",
  "Germany", "Ghana", "Greece", "Guatemala", "Guinea", "Hong Kong", "Hungary", "India",
  "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast", "Jamaica",
  "Japan", "Jordan", "Kenya", "Kiribati", "Kuwait", "Laos", "Latvia", "Lebanon", "Liberia",
  "Libya", "Lithuania", "Malaysia", "Malta", "Mauritius", "Mexico", "Moldova", "Mongolia",
  "Montenegro", "Morocco", "Mozambique", "Myanmar", "Nauru", "Nepal", "Netherlands",
  "New Caledonia", "New Zealand", "Nigeria", "North Macedonia", "Norway", "Pakistan",
  "Palestine", "Papua New Guinea", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Rwanda", "Samoa", "Saudi Arabia", "Senegal", "Serbia",
  "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
  "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Sweden",
  "Switzerland", "Syria", "Taiwan", "Tanzania", "Thailand", "Timor-Leste", "Tonga",
  "Tunisia", "Turkey", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States of America", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam",
  "Yemen", "Zambia", "Zimbabwe",
];

// The languages Victorian courts actually book interpreters for, then the rest. Auslan is
// included because the interpreter question covers it, and the form asks the same question
// whether the interpreter is spoken or signed.
export const LANGUAGES = [
  "Arabic", "Auslan", "Vietnamese", "Mandarin", "Cantonese", "Greek", "Italian", "Turkish",
  "Dari", "Persian (Farsi)", "Punjabi", "Hindi", "Urdu", "Somali", "Amharic", "Tigrinya",
  "Dinka", "Khmer", "Thai", "Filipino (Tagalog)", "Spanish", "Portuguese", "Russian",
  "Serbian", "Croatian", "Bosnian", "Macedonian", "Albanian", "Polish", "Romanian",
  "Samoan", "Tongan", "Maori", "Afrikaans", "Bengali", "Burmese", "Bulgarian", "Czech",
  "Dutch", "Fijian", "French", "German", "Gujarati", "Hakha Chin", "Hazaragi", "Hebrew",
  "Hungarian", "Indonesian", "Japanese", "Karen", "Kinyarwanda", "Korean", "Kurdish",
  "Lao", "Latvian", "Lithuanian", "Malay", "Malayalam", "Maltese", "Mongolian", "Nepali",
  "Nuer", "Oromo", "Pashto", "Rohingya", "Shona", "Sinhalese", "Slovak", "Slovenian",
  "Swahili", "Tamil", "Telugu", "Tetum", "Ukrainian", "Uyghur",
];

function toOptions(values) {
  return values.map((value) => ({ value, label: value }));
}

export const COUNTRY_OPTIONS = toOptions(COUNTRIES);
export const LANGUAGE_OPTIONS = toOptions(LANGUAGES);

/**
 * The list, plus whatever is already recorded if the list does not contain it.
 *
 * Applications already answered carry free text - a spelling the list does not have, a
 * country that no longer exists, a language written a different way. A plain select would
 * show those as blank and save the blank the next time the form was touched, losing an
 * answer the client gave without anyone seeing it happen. Keeping the current value as its
 * own option means the form can only ever gain structure, never lose an answer.
 */
export function withCurrentValue(options, value) {
  const current = String(value ?? "").trim();
  if (!current) return options;
  if (options.some((option) => option.value === current)) return options;
  return [{ value: current, label: current }, ...options];
}

/** True when a stored arrival value is a date the date input can show. */
export function isIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value ?? "").trim());
}

// Victorian courts, written the way a listing is written on a file.
//
// The court field was free text, so one venue arrived as "Melb Mags", "Melbourne
// Magistrates" and "MMC", and nothing could be grouped or matched on it. The higher and
// specialist courts are here because a listing is not always a Magistrates' one, and a
// venue the list does not carry is still kept by withCurrentValue.
export const VICTORIAN_COURTS = [
  "Melbourne Magistrates' Court",
  "Broadmeadows Magistrates' Court",
  "Dandenong Magistrates' Court",
  "Frankston Magistrates' Court",
  "Heidelberg Magistrates' Court",
  "Moorabbin Justice Centre",
  "Ringwood Magistrates' Court",
  "Sunshine Magistrates' Court",
  "Werribee Magistrates' Court",
  "Ballarat Magistrates' Court",
  "Bendigo Magistrates' Court",
  "Geelong Magistrates' Court",
  "Latrobe Valley Magistrates' Court",
  "Shepparton Magistrates' Court",
  "Mildura Magistrates' Court",
  "Wangaratta Magistrates' Court",
  "Warrnambool Magistrates' Court",
  "Wodonga Magistrates' Court",
  "Horsham Magistrates' Court",
  "Bairnsdale Magistrates' Court",
  "Sale Magistrates' Court",
  "Seymour Magistrates' Court",
  "Swan Hill Magistrates' Court",
  "Echuca Magistrates' Court",
  "Colac Magistrates' Court",
  "Hamilton Magistrates' Court",
  "Portland Magistrates' Court",
  "Wonthaggi Magistrates' Court",
  "Korumburra Magistrates' Court",
  "Benalla Magistrates' Court",
  "Ararat Magistrates' Court",
  "Castlemaine Magistrates' Court",
  "Kyneton Magistrates' Court",
  "Maryborough Magistrates' Court",
  "Stawell Magistrates' Court",
  "Neighbourhood Justice Centre, Collingwood",
  "County Court of Victoria",
  "Supreme Court of Victoria",
  "Court of Appeal",
  "Children's Court of Victoria",
  "Coroners Court of Victoria",
  "Victorian Civil and Administrative Tribunal",
];

export const COURT_OPTIONS = toOptions(VICTORIAN_COURTS);

const CLIENT_NAME_KEYS = [
  "clientName", "client_name", "fullName", "full_name", "clientFullName", "name",
];
const FILE_NUMBER_KEYS = [
  "fileNumber", "file_number", "file_no", "fileNo", "matterNumber", "matter_number",
  "matterId", "matter_id",
];

function firstValue(data, keys) {
  for (const key of keys) {
    const value = String(data?.[key] ?? "").trim();
    if (value) return value;
  }
  return "";
}

/**
 * A name for a draft, taken from what the form already knows.
 *
 * Drafts were saved under whatever somebody typed, so a list of them reads "test", "aa",
 * "draft 2", and nobody can tell whose file each belongs to. Forms name their fields
 * differently, hence the several keys, and some hold the first and last name apart.
 *
 * Returns "" when the form knows neither, so the field stays empty and asks, rather than
 * offering a label that means nothing.
 */
export function suggestDraftLabel(formData) {
  const data = formData && typeof formData === "object" ? formData : {};

  let client = firstValue(data, CLIENT_NAME_KEYS);
  if (!client) {
    client = [
      firstValue(data, ["first_name", "firstName", "given_names", "givenNames"]),
      firstValue(data, ["last_name", "lastName", "surname", "familyName"]),
    ].filter(Boolean).join(" ");
  }

  const fileNumber = firstValue(data, FILE_NUMBER_KEYS);
  return [client, fileNumber].filter(Boolean).join(" - ");
}

// Where a court sits, for screens that already ask which court separately.
//
// The Aid Planner asks for the court type - Magistrates', County - and then the location
// beside it, so the full "Melbourne Magistrates' Court" would say the same thing twice.
export const COURT_LOCATIONS = [
  "Melbourne", "Broadmeadows", "Dandenong", "Frankston", "Heidelberg", "Moorabbin",
  "Ringwood", "Sunshine", "Werribee", "Ballarat", "Bendigo", "Geelong", "Latrobe Valley",
  "Shepparton", "Mildura", "Wangaratta", "Warrnambool", "Wodonga", "Horsham", "Bairnsdale",
  "Sale", "Seymour", "Swan Hill", "Echuca", "Colac", "Hamilton", "Portland", "Wonthaggi",
  "Korumburra", "Benalla", "Ararat", "Castlemaine", "Kyneton", "Maryborough", "Stawell",
  "Collingwood (Neighbourhood Justice Centre)",
];

/**
 * The same preservation rule as withCurrentValue, for screens whose lists are plain
 * strings rather than {value, label} pairs.
 */
export function withCurrentString(values, value) {
  const current = String(value ?? "").trim();
  if (!current) return values;
  return values.includes(current) ? values : [current, ...values];
}
